import { useEffect, useRef, useState } from 'react';
import { REFRESH_EVENT } from './useListRefresh';

interface Options {
  /** Distance (px) the pull needs to exceed before release triggers a refresh. */
  threshold?: number;
  /** Distance (px) of resisted pull = finger travel * factor. */
  resistance?: number;
  /** Scrollable document root (defaults to the page/document scroll). */
  enabled?: boolean;
}

const TRIGGERED_EVENT = 'catcare:pull-refresh';

/**
 * Google/Facebook-style pull-to-refresh gesture for the admin app.
 *
 * Attaches touch listeners to the whole page. When the page is scrolled to the
 * very top and the user drags down past `threshold`, a spinner is shown and, on
 * release, a `catcare:refresh` window event is dispatched (the hooks / pages
 * that listen to it reload their data). A `catcare:pull-refresh` event is also
 * dispatched first so screens without a list hook can reload individually.
 */
export function usePullToRefresh({ threshold = 72, resistance = 0.35, enabled = true }: Options = {}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const pulling = useRef(false);
  const pullRef = useRef(0);
  const thresholdRef = useRef(threshold);

  useEffect(() => {
    thresholdRef.current = threshold;
  }, [threshold]);

  useEffect(() => {
    if (!enabled) return;

    const atTop = () => {
      const doc = document.scrollingElement || document.documentElement;
      return (doc.scrollTop ?? 0) <= 0;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!enabled || !atTop() || refreshing) return;
      // Only start a pull-down when there's nowhere above to scroll and the user
      // is dragging downward.
      startY.current = e.touches[0]?.clientY ?? null;
      pulling.current = startY.current !== null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!enabled || !pulling.current || refreshing) return;
      if (startY.current === null) return;
      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startY.current;
      if (delta <= 0 || !atTop()) {
        setPull(0);
        pullRef.current = 0;
        return;
      }
      const resisted = delta * resistance;
      pullRef.current = Math.min(resisted, 120);
      setPull(pullRef.current);
    };

    const onTouchEnd = () => {
      if (!enabled || !pulling.current) return;
      pulling.current = false;
      startY.current = null;
      if (pullRef.current >= thresholdRef.current && !refreshing) {
        setRefreshing(true);
        window.dispatchEvent(new CustomEvent(TRIGGERED_EVENT));
        window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
        // Allow enough time for spinners to settle before hiding; the actual
        // data reload is async and independent.
        window.setTimeout(() => {
          setPull(0);
          pullRef.current = 0;
          setRefreshing(false);
        }, 850);
      } else {
        setPull(0);
        pullRef.current = 0;
      }
    };

    const onScroll = () => {
      if (pullRef.current > 0 && !atTop()) {
        setPull(0);
        pullRef.current = 0;
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('scroll', onScroll);
    };
  }, [enabled, refreshing]);

  return { pull, refreshing };
}

export { TRIGGERED_EVENT };
